// src/views/activity-log/ActivityLogView.jsx
// Phase 40 Plan 04 Task 2 — Settings sub-page. AU-04. UI-SPEC #9. RESEARCH.md Pattern 10.
//
// Full-screen overlay (NOT a top-level view) navigated to from Settings →
// Account → "Activity log" row. Renders newest-first paginated 50/page from
// the Worker GET /auth/me/activity-log endpoint with `before` cursor.

import React, { useState, useEffect, useCallback } from 'react';
import { ChevronLeft, Calendar } from 'lucide-react';
import { fetchActivityLog } from '../../lib/sync.js';
import ActivityLogRow from './ActivityLogRow.jsx';

const PAGE_SIZE = 50;

export default function ActivityLogView({ jwt, currentUserId = null, onBack }) {
  const [entries, setEntries] = useState([]);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [loadingMore, setLoadingMore] = useState(false);

  const loadFirstPage = useCallback(async () => {
    if (!jwt) {
      setLoading(false);
      setEntries([]);
      setHasMore(false);
      return;
    }
    try {
      const data = await fetchActivityLog({ jwt, limit: PAGE_SIZE });
      setEntries(Array.isArray(data?.entries) ? data.entries : []);
      setHasMore(!!data?.has_more);
    } catch (e) {
      setError(e?.message || 'Failed to load activity log');
    } finally {
      setLoading(false);
    }
  }, [jwt]);

  useEffect(() => { loadFirstPage(); }, [loadFirstPage]);

  const loadMore = async () => {
    if (loadingMore || !hasMore || entries.length === 0) return;
    setLoadingMore(true);
    const oldest = entries[entries.length - 1];
    try {
      const data = await fetchActivityLog({ jwt, before: oldest?.created_at, limit: PAGE_SIZE });
      const next = Array.isArray(data?.entries) ? data.entries : [];
      setEntries((prev) => [...prev, ...next]);
      setHasMore(!!data?.has_more);
    } catch (e) {
      setError(e?.message || 'Failed to load more');
    } finally {
      setLoadingMore(false);
    }
  };

  return (
    <div
      role="region"
      aria-label="Activity log"
      style={{ position: 'fixed', inset: 0, background: 'var(--c-page)', zIndex: 2200, display: 'flex', flexDirection: 'column' }}
    >
      {/* Sticky header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '14px 16px', borderBottom: '1px solid var(--c-border)', background: 'var(--c-bg)', position: 'sticky', top: 0 }}>
        <button onClick={onBack} aria-label="Back to settings"
          style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--c-text-2)', padding: 6, borderRadius: 6, minWidth: 44, minHeight: 44, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <ChevronLeft size={20} />
        </button>
        <div style={{ flex: 1, minWidth: 0 }}>
          <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: 'var(--font-h2)', color: 'var(--c-heading)', margin: 0, fontWeight: 600 }}>Activity log</h2>
          {!loading && entries.length > 0 && (
            <small style={{ color: 'var(--c-text-3)', fontSize: 'var(--font-tiny)', textTransform: 'uppercase', letterSpacing: '0.07em' }} className="t-meta">
              <em>{entries.length}</em> entries
            </small>
          )}
        </div>
      </div>

      {/* Body */}
      <div style={{ flex: 1, overflowY: 'auto', padding: 16 }}>
        {loading ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {[0, 1, 2].map((i) => (
              <div key={i} style={{ background: 'var(--c-surface)', borderRadius: 10, height: 64, opacity: 0.6 - i * 0.15, animation: 'tendPulse 600ms ease-in-out infinite alternate' }} />
            ))}
          </div>
        ) : error ? (
          <div style={{ textAlign: 'center', marginTop: 48 }}>
            <p style={{ color: 'var(--c-danger)', fontSize: 'var(--font-body-size)' }}>{error}</p>
            <button onClick={loadFirstPage} style={{ marginTop: 12, background: 'var(--c-btn-bg)', color: 'var(--c-btn-text)', border: 'none', borderRadius: 20, padding: '8px 16px', fontSize: '0.8125rem', fontWeight: 600, cursor: 'pointer' }}>
              Try again
            </button>
          </div>
        ) : entries.length === 0 ? (
          <div style={{ textAlign: 'center', marginTop: 48 }}>
            <Calendar size={48} style={{ color: 'var(--c-text-3)' }} />
            <h3 style={{ fontFamily: "'Playfair Display', serif", fontSize: 'var(--font-h3)', color: 'var(--c-heading)', margin: '12px 0 6px', fontWeight: 600 }}>No activity yet</h3>
            <p style={{ fontSize: 'var(--font-body-size)', color: 'var(--c-text-2)', margin: 0, lineHeight: 1.55 }}>
              Changes you make to your garden will show here.
            </p>
          </div>
        ) : (
          <>
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              {entries.map((entry) => (
                <ActivityLogRow key={entry.id} entry={entry} currentUserId={currentUserId} />
              ))}
            </div>
            <button onClick={loadMore} disabled={!hasMore || loadingMore}
              style={{
                marginTop: 16,
                width: '100%',
                background: 'var(--c-btn-bg)',
                color: 'var(--c-btn-text)',
                border: 'none',
                borderRadius: 20,
                padding: '10px 16px',
                fontSize: 'var(--font-body-size)',
                fontWeight: 600,
                cursor: hasMore && !loadingMore ? 'pointer' : 'default',
                opacity: hasMore && !loadingMore ? 1 : 0.6,
                minHeight: 44,
              }}>
              {loadingMore ? 'Loading…' : (hasMore ? 'Load more' : 'All caught up.')}
            </button>
          </>
        )}
      </div>
    </div>
  );
}
